import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { money, formatDateAT } from "@/lib/format";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { AccessDenied, canViewFinance } from "@/lib/permissions";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer,
  ReferenceLine, CartesianGrid, Area, ComposedChart,
} from "recharts";
import {
  TrendingUp, TrendingDown, AlertTriangle, Zap, Info, RotateCcw,
  Plus, Percent, Clock,
} from "lucide-react";

const HORIZONS = [7, 30, 60, 90];

const T = {
  tr: {
    title: "Nakit Akış Tahmini", subtitle: "Deterministik hesaplama · AI sadece açıklar, hesaplamaz.",
    horizon: "Ufuk", baseForecast: "Temel Tahmin", whatIf: "Ne-olur-ise Simülasyonu",
    starting: "Başlangıç Bakiye", ending: "Bitiş Bakiye", minBalance: "Min. Bakiye",
    minBalanceDate: "Min. Gün", totalIn: "Toplam Gelir", totalOut: "Toplam Gider",
    net: "Net", avgDaily: "Ort. Günlük Ciro",
    timeline: "Gün-be-gün Zaman Çizelgesi", warnings: "Uyarılar",
    date: "Tarih", opening: "Açılış", income: "Gelir", expense: "Gider", closing: "Kapanış",
    events: "Olaylar", noEvents: "Olay yok",
    scenarios: "Senaryolar", clearScenarios: "Temizle", runSim: "Simüle Et",
    revChange: "Ciro % değişim", expChange: "Gider % değişim",
    addExpense: "Ek Gider Ekle", expName: "Ad", expAmount: "Tutar", expDate: "Tarih",
    healthGreen: "Sağlıklı", healthYellow: "Dikkat", healthRed: "Kritik",
    loading: "Yükleniyor…", noData: "Veri yok",
    scenarioApplied: "Senaryo uygulandı", scenarioCleared: "Senaryolar temizlendi",
    reset: "Sıfırla", explain: "Copilot'a Sor",
    day: "gün", days: "gün", starts: "Şuradan başla",
    critical: "Kritik", warn: "Uyarı", info: "Bilgi",
    ap: "Fatura Ödemesi", ar: "Fatura Tahsilatı", recurring: "Sabit Gider",
    historical: "Beklenen Ciro", avg_expense: "Beklenen Gider", scenario: "Senaryo",
    sources: "Kaynaklar", ap_inv: "AP faturaları", ar_inv: "AR faturaları",
    rec_exp: "Sabit gider", hist_days: "Ciro günü",
  },
  de: {
    title: "Cashflow-Prognose", subtitle: "Deterministische Berechnung · KI erklärt nur, rechnet nicht.",
    horizon: "Horizont", baseForecast: "Basisprognose", whatIf: "Was-wäre-wenn Simulator",
    starting: "Anfangssaldo", ending: "Endsaldo", minBalance: "Min. Saldo",
    minBalanceDate: "Min. Tag", totalIn: "Einnahmen gesamt", totalOut: "Ausgaben gesamt",
    net: "Netto", avgDaily: "Ø Tagesumsatz",
    timeline: "Tag-für-Tag Timeline", warnings: "Warnungen",
    date: "Datum", opening: "Anfang", income: "Einnahmen", expense: "Ausgaben", closing: "Ende",
    events: "Ereignisse", noEvents: "Keine Ereignisse",
    scenarios: "Szenarien", clearScenarios: "Löschen", runSim: "Simulieren",
    revChange: "Umsatz % Änderung", expChange: "Ausgaben % Änderung",
    addExpense: "Ausgabe hinzufügen", expName: "Name", expAmount: "Betrag", expDate: "Datum",
    healthGreen: "Gesund", healthYellow: "Achtung", healthRed: "Kritisch",
    loading: "Lädt…", noData: "Keine Daten",
    scenarioApplied: "Szenario angewendet", scenarioCleared: "Szenarien gelöscht",
    reset: "Zurücksetzen", explain: "Copilot fragen",
    day: "Tag", days: "Tage", starts: "Startet am",
    critical: "Kritisch", warn: "Warnung", info: "Info",
    ap: "Rechnung zu zahlen", ar: "Rechnung zu erhalten", recurring: "Fixkosten",
    historical: "Erwarteter Umsatz", avg_expense: "Erwartete Ausgaben", scenario: "Szenario",
    sources: "Datenquellen", ap_inv: "AP-Rechnungen", ar_inv: "AR-Rechnungen",
    rec_exp: "Fixkosten", hist_days: "Umsatztage",
  },
};

function healthColor(h) {
  if (h === "green") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (h === "yellow") return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-rose-100 text-rose-800 border-rose-200";
}
function healthDot(h) {
  if (h === "green") return "bg-emerald-500";
  if (h === "yellow") return "bg-amber-500";
  return "bg-rose-500";
}
function severityBadge(s) {
  if (s === "critical") return "border-rose-300 bg-rose-50 text-rose-900";
  if (s === "warning") return "border-amber-300 bg-amber-50 text-amber-900";
  return "border-sky-300 bg-sky-50 text-sky-900";
}

function StatCard({ label, value, sub, testid, icon: Icon, tone = "" }) {
  return (
    <div className={`rounded-xl border p-4 bg-background ${tone}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
        <span>{label}</span>
      </div>
      <div className="mt-1 text-xl font-semibold">{value}</div>
      {sub ? <div className="text-xs text-muted-foreground mt-0.5">{sub}</div> : null}
    </div>
  );
}

export default function ForecastPage() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const denied = !canViewFinance(user);
  const t = T[lang] || T.tr;
  const [horizon, setHorizon] = useState(30);
  const [loading, setLoading] = useState(true);
  const [base, setBase] = useState(null);
  const [simulated, setSimulated] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  // Simulator inputs
  const [revPct, setRevPct] = useState("");
  const [expPct, setExpPct] = useState("");
  const [addExpName, setAddExpName] = useState("");
  const [addExpAmount, setAddExpAmount] = useState("");
  const [addExpDate, setAddExpDate] = useState(new Date().toISOString().slice(0, 10));
  const [simLoading, setSimLoading] = useState(false);

  const activeForecast = simulated || base;

  const loadBase = async (h) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/forecast?horizon_days=${h}`);
      setBase(data);
      // If scenarios are active, re-run simulate for the new horizon
      if (scenarios.length > 0) {
        const { data: sim } = await api.post(`/forecast/simulate`, { horizon_days: h, scenarios });
        setSimulated(sim);
      } else {
        setSimulated(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (denied) return;
    loadBase(horizon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horizon, denied]);

  const runSimulate = async (extra = []) => {
    const list = [...scenarios, ...extra].filter(Boolean);
    setSimLoading(true);
    try {
      const { data } = await api.post(`/forecast/simulate`, { horizon_days: horizon, scenarios: list });
      setSimulated(data);
      setScenarios(list);
      toast.success(t.scenarioApplied);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sim failed");
    } finally {
      setSimLoading(false);
    }
  };

  const clearScenarios = async () => {
    setScenarios([]);
    setSimulated(null);
    setRevPct(""); setExpPct(""); setAddExpName(""); setAddExpAmount("");
    toast.info(t.scenarioCleared);
  };

  const applyRevPct = () => {
    const p = parseFloat(revPct);
    if (!Number.isFinite(p) || p === 0) return;
    runSimulate([{ type: "revenue_pct_change", pct: p }]);
  };
  const applyExpPct = () => {
    const p = parseFloat(expPct);
    if (!Number.isFinite(p) || p === 0) return;
    runSimulate([{ type: "expense_pct_change", pct: p }]);
  };
  const applyAddExpense = () => {
    const a = parseFloat(addExpAmount);
    if (!Number.isFinite(a) || a <= 0) return;
    runSimulate([{ type: "add_expense", name: addExpName || "Ad-hoc", amount: a, date: addExpDate }]);
    setAddExpName(""); setAddExpAmount("");
  };

  const chartData = useMemo(() => {
    if (!activeForecast) return [];
    return activeForecast.timeline.map(d => ({
      date: d.date.slice(5), // MM-DD
      closing: d.closing,
      income: d.income,
      expense: -d.expense,
    }));
  }, [activeForecast]);

  const summary = activeForecast?.summary;
  const sources = activeForecast?.sources || {};

  if (denied) return <AccessDenied testid="forecast-access-denied" />;

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6" data-testid="forecast-page">
      <PageHeader title={t.title} description={t.subtitle} />

      {/* Horizon tabs */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="forecast-horizon-tabs">
        {HORIZONS.map(h => (
          <Button
            key={h} size="sm"
            variant={horizon === h ? "default" : "outline"}
            onClick={() => setHorizon(h)}
            data-testid={`forecast-horizon-${h}`}
          >
            {h} {t.days}
          </Button>
        ))}
        {(simulated || scenarios.length > 0) && (
          <Button size="sm" variant="ghost" onClick={clearScenarios} data-testid="forecast-reset">
            <RotateCcw className="h-4 w-4 mr-1" /> {t.reset}
          </Button>
        )}
        <div className="ml-auto">
          {summary && (
            <Badge className={`${healthColor(summary.health)} border`} data-testid={`forecast-health-${summary.health}`}>
              <span className={`inline-block w-2 h-2 rounded-full mr-2 ${healthDot(summary.health)}`} />
              {summary.health === "green" ? t.healthGreen : summary.health === "yellow" ? t.healthYellow : t.healthRed}
            </Badge>
          )}
        </div>
      </div>

      {loading && !summary ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : summary ? (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="forecast-summary">
            <StatCard testid="forecast-starting" label={t.starting} value={money(summary.starting_balance)} icon={Clock} />
            <StatCard
              testid="forecast-ending" label={t.ending}
              value={money(summary.ending_balance)}
              sub={summary.ending_balance < 0 ? "⚠️" : ""}
              icon={summary.ending_balance >= summary.starting_balance ? TrendingUp : TrendingDown}
              tone={summary.ending_balance < 0 ? "border-rose-300" : ""}
            />
            <StatCard
              testid="forecast-min" label={t.minBalance}
              value={money(summary.min_balance)}
              sub={`${t.minBalanceDate}: ${formatDateAT(summary.min_balance_date)}`}
              icon={AlertTriangle}
              tone={summary.min_balance < 0 ? "border-rose-300" : ""}
            />
            <StatCard
              testid="forecast-net" label={t.net}
              value={money(summary.net)}
              sub={`↑ ${money(summary.total_income)} · ↓ ${money(summary.total_expense)}`}
              icon={summary.net >= 0 ? TrendingUp : TrendingDown}
            />
          </div>

          {/* Chart */}
          <Card data-testid="forecast-chart-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t.baseForecast} · {horizon} {t.days}</CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="date" fontSize={10} />
                    <YAxis fontSize={10} />
                    <ReferenceLine y={0} stroke="#c11" strokeDasharray="2 2" />
                    <RTooltip formatter={(v) => money(v)} />
                    <Line
                      type="monotone" dataKey="closing" strokeWidth={2}
                      stroke="#0ea5e9" dot={false} name={t.closing}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1" data-testid="forecast-sources">
                <span>{t.sources}:</span>
                <span>{t.ap_inv}: {sources.ap_invoices ?? 0}</span>
                <span>{t.ar_inv}: {sources.ar_invoices ?? 0}</span>
                <span>{t.rec_exp}: {sources.recurring_expenses ?? 0}</span>
                <span>{t.hist_days}: {sources.historical_days_with_revenue ?? 0}</span>
              </div>
            </CardContent>
          </Card>

          {/* Simulator */}
          <Card data-testid="forecast-simulator">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className="h-4 w-4 text-amber-500" /> {t.whatIf}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded-lg border p-3 space-y-2" data-testid="sim-rev-block">
                  <Label className="text-xs">{t.revChange}</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number" placeholder="-20"
                      value={revPct} onChange={(e) => setRevPct(e.target.value)}
                      data-testid="sim-rev-input"
                    />
                    <Button size="sm" onClick={applyRevPct} disabled={simLoading} data-testid="sim-rev-apply">
                      <Percent className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="rounded-lg border p-3 space-y-2" data-testid="sim-exp-block">
                  <Label className="text-xs">{t.expChange}</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number" placeholder="+10"
                      value={expPct} onChange={(e) => setExpPct(e.target.value)}
                      data-testid="sim-exp-input"
                    />
                    <Button size="sm" onClick={applyExpPct} disabled={simLoading} data-testid="sim-exp-apply">
                      <Percent className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="rounded-lg border p-3 space-y-2" data-testid="sim-addexp-block">
                  <Label className="text-xs">{t.addExpense}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder={t.expName} value={addExpName}
                      onChange={(e) => setAddExpName(e.target.value)}
                      data-testid="sim-addexp-name"
                    />
                    <Input
                      type="number" placeholder={t.expAmount} value={addExpAmount}
                      onChange={(e) => setAddExpAmount(e.target.value)}
                      data-testid="sim-addexp-amount"
                    />
                    <Input
                      type="date" value={addExpDate}
                      onChange={(e) => setAddExpDate(e.target.value)}
                      className="col-span-1"
                      data-testid="sim-addexp-date"
                    />
                    <Button size="sm" onClick={applyAddExpense} disabled={simLoading} data-testid="sim-addexp-apply">
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
              {scenarios.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="sim-active-list">
                  {scenarios.map((s, i) => (
                    <Badge key={i} variant="outline" className="border-dashed" data-testid={`sim-tag-${i}`}>
                      {s.type}{s.pct != null ? ` ${s.pct}%` : ""}{s.amount != null ? ` €${s.amount}` : ""}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Warnings */}
          {activeForecast.warnings?.length > 0 && (
            <Card data-testid="forecast-warnings">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t.warnings} · {activeForecast.warnings.length}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-64 overflow-y-auto">
                {activeForecast.warnings.slice(0, 25).map((w, i) => (
                  <div
                    key={i}
                    className={`rounded-lg border px-3 py-2 text-sm flex items-start gap-2 ${severityBadge(w.severity)}`}
                    data-testid={`forecast-warning-${i}`}
                  >
                    {w.severity === "critical" ? <AlertTriangle className="h-4 w-4 mt-0.5" /> :
                      w.severity === "warning" ? <AlertTriangle className="h-4 w-4 mt-0.5" /> :
                        <Info className="h-4 w-4 mt-0.5" />}
                    <div className="flex-1">
                      <div className="font-medium">{formatDateAT(w.date)} · {w.code}</div>
                      <div className="text-xs opacity-80">{w.message}</div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Timeline table */}
          <Card data-testid="forecast-timeline-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t.timeline}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="forecast-timeline-table">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left px-3 py-2">{t.date}</th>
                      <th className="text-right px-3 py-2">{t.opening}</th>
                      <th className="text-right px-3 py-2 text-emerald-700">{t.income}</th>
                      <th className="text-right px-3 py-2 text-rose-700">{t.expense}</th>
                      <th className="text-right px-3 py-2">{t.closing}</th>
                      <th className="text-left px-3 py-2">{t.events}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeForecast.timeline.map((d, i) => (
                      <tr
                        key={d.date}
                        className={`border-t ${d.closing < 0 ? "bg-rose-50/40" : ""}`}
                        data-testid={`forecast-row-${i}`}
                      >
                        <td className="px-3 py-2 whitespace-nowrap">{formatDateAT(d.date)}</td>
                        <td className="px-3 py-2 text-right">{money(d.opening)}</td>
                        <td className="px-3 py-2 text-right text-emerald-700">{money(d.income)}</td>
                        <td className="px-3 py-2 text-right text-rose-700">{money(d.expense)}</td>
                        <td className={`px-3 py-2 text-right font-medium ${d.closing < 0 ? "text-rose-700" : ""}`}>
                          {money(d.closing)}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {d.events?.length ? (
                            <div className="flex flex-wrap gap-1">
                              {d.events.slice(0, 4).map((ev, j) => (
                                <span key={j} className="inline-block px-2 py-0.5 rounded border bg-background">
                                  {ev.kind === "income" ? "↑" : "↓"} {ev.label} · {money(ev.amount)}
                                </span>
                              ))}
                              {d.events.length > 4 ? <span>+{d.events.length - 4}</span> : null}
                            </div>
                          ) : (
                            <span className="opacity-60">{t.noEvents}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="text-muted-foreground text-sm" data-testid="forecast-empty">{t.noData}</div>
      )}
    </div>
  );
}
